/**
 * The Daily log's pane for a walk's Photos row (LED-1152, design "Daily log ·
 * detail", Photos row selected): a 3-up gallery of the walk's photos on the
 * log; tapping one focuses it below — the large image, its caption (editable,
 * saved on blur) and its location ("Add location").
 *
 * The writes are the Walk photos card's own (LED-1154): the caption goes to
 * the mobile manager's `updatePhotoCaption`, the location to the photo's
 * location — and the location picker IS that card's (`LocationMenu`).
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { MapPin, X } from "lucide-react";
import {
  updateWalkPhotoDescription, updateWalkPhotoLocation,
  type ConsoleWalkPhoto, type DailyLogWalkRow, type WalkPhotoLocation,
} from "../api";
import { DL, FONT_MONO, FONT_TEXT } from "../tokens";
import { ASK_COMPOSER_CLEARANCE, clockOf, dayLabel } from "../components/chrome";
import { PhotoImage } from "../components/PhotoImage";
import { LocationMenu } from "./WalkPhotosPane";
import { PANE_ICON_BUTTON, PANE_MONO, usePaneHeight } from "./DailyLogEntryEditor";

type Edit = { description?: string; location?: WalkPhotoLocation };

export function DailyLogPhotosPane({
  walk, photos, jobSiteId, search, focusId, onFocus, onClose, onChanged,
}: {
  walk: DailyLogWalkRow;
  photos: ConsoleWalkPhoto[];
  jobSiteId: string;
  search: string;
  /** The photo shown large below the grid, or none. */
  focusId: string | null;
  onFocus: (mediaId: string | null) => void;
  onClose: () => void;
  /** A caption or location landed; the host may re-read the walk's photos. */
  onChanged?: () => void;
}) {
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [locOpen, setLocOpen] = useState(false);
  const shown = photos.map((photo) => ({ ...photo, ...edits[photo.mediaId] }));
  const focus = shown.find((photo) => photo.mediaId === focusId) ?? null;
  const paneRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLDivElement>(null);
  const height = usePaneHeight(paneRef, walk.visitId);
  // Bring the focused photo into the PANE's view — never the page's, which
  // would move the tables under the reader.
  useEffect(() => {
    setLocOpen(false);
    const body = bodyRef.current;
    const target = focusRef.current;
    if (!focusId || !body || !target) return;
    const top = target.offsetTop - 12;
    if (top > body.scrollTop + body.clientHeight - 120 || top < body.scrollTop) body.scrollTo?.({ top, behavior: "smooth" });
  }, [focusId]);
  // The location menu hangs below its trigger, inside the pane's scrolling
  // body: bring it into view there rather than letting the pane clip it.
  useEffect(() => {
    if (!locOpen) return;
    const frame = requestAnimationFrame(() => {
      const body = bodyRef.current;
      body?.scrollTo?.({ top: body.scrollHeight, behavior: "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [locOpen]);

  const saveDescription = async (photo: ConsoleWalkPhoto, text: string) => {
    const next = text.trim();
    if (!next || next === photo.description) return;
    const previous = photo.description;
    setProblem(null);
    setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], description: next } }));
    const result = await updateWalkPhotoDescription(walk.visitId, photo.mediaId, search, next);
    if (!result.ok) {
      setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], description: previous } }));
      setProblem(result.error ?? "Couldn’t save the caption.");
    } else onChanged?.();
  };
  const saveLocation = async (photo: ConsoleWalkPhoto, location: WalkPhotoLocation) => {
    const previous = photo.location;
    setLocOpen(false);
    setProblem(null);
    setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], location } }));
    const result = await updateWalkPhotoLocation(walk.visitId, photo.mediaId, search, location);
    if (!result.ok) {
      setEdits((all) => ({ ...all, [photo.mediaId]: { ...all[photo.mediaId], location: previous } }));
      setProblem(result.error ?? "Couldn’t save the location.");
    } else onChanged?.();
  };

  const when = walk.startedAt ? clockOf(new Date(walk.startedAt)) : "";
  const sub = [`${photos.length} ${photos.length === 1 ? "photo" : "photos"}`, when, walk.walkerName].filter(Boolean).join(" · ");

  return (
    <div ref={paneRef} data-testid="daily-log-photos-pane" data-visit-id={walk.visitId} style={{ ...paneStyle, maxHeight: height ?? `calc(100dvh - ${ASK_COMPOSER_CLEARANCE + 24}px)` }}>
      <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8, height: 46, padding: "0 10px 0 16px", borderBottom: `1px solid ${DL.hairline}` }}>
        <span style={{ ...PANE_MONO, fontSize: 9.5, letterSpacing: "1.2px", color: DL.muted }}>Walk · Photos</span>
        <span style={{ flex: 1 }} />
        <button type="button" className="kb dl-icon" data-testid="daily-log-photos-close" aria-label="Close the photos" onClick={onClose} style={PANE_ICON_BUTTON}>
          <X size={14} color={DL.ink2} strokeWidth={2} aria-hidden />
        </button>
      </div>
      {/* While the location menu is open the body keeps room for it below the
          trigger (it is positioned, so it would not otherwise count), and the
          pane grows or scrolls to show it instead of clipping it. */}
      <div ref={bodyRef} className="noscroll" style={{ position: "relative", flex: 1, minHeight: 0, overflowY: "auto", padding: `16px 18px ${locOpen ? 300 : 20}px` }}>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px", lineHeight: 1.25 }}>{walk.name || "Untitled walk"}</div>
        <div style={{ fontSize: 13, color: DL.sub, marginTop: 4 }}>{sub}</div>
        <div data-testid="daily-log-photos-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginTop: 14 }}>
          {shown.map((photo, index) => {
            const on = photo.mediaId === focusId;
            return (
              <button
                key={photo.mediaId}
                type="button"
                className="kb dl-thumb"
                data-testid="daily-log-photos-tile"
                data-media-id={photo.mediaId}
                aria-pressed={on}
                aria-label={`Photo ${index + 1} of ${photos.length}${photo.description ? `: ${photo.description}` : ""}`}
                onClick={() => onFocus(on ? null : photo.mediaId)}
                style={{ position: "relative", aspectRatio: "1", padding: 0, border: 0, borderRadius: 10, overflow: "hidden", background: DL.photoGround, cursor: "zoom-in", boxShadow: on ? `0 0 0 2px ${DL.ink}` : "none" }}
              >
                <PhotoImage src={photo.thumbnailUrl ?? photo.url ?? undefined} alt="" style={coverImage} />
                <span style={{ position: "absolute", left: 6, bottom: 6, fontFamily: FONT_MONO, fontSize: 8, fontWeight: 700, color: DL.card, background: DL.overlay, padding: "2px 5px", borderRadius: 4 }}>{clockOf(new Date(photo.timestamp))}</span>
              </button>
            );
          })}
        </div>
        {focus && (
          <div ref={focusRef} data-testid="daily-log-photo-focus" data-media-id={focus.mediaId} style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${DL.hairline}` }}>
            <div style={{ position: "relative", aspectRatio: "4 / 3", borderRadius: 12, overflow: "hidden", background: DL.photoGround }}>
              <PhotoImage src={focus.url ?? focus.thumbnailUrl ?? undefined} alt={focus.description || "Walk photo"} style={coverImage} />
              <span style={{ position: "absolute", left: 10, top: 10, fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, color: DL.card, background: DL.overlay, padding: "4px 7px", borderRadius: 5, textTransform: "uppercase" }}>
                {`${dayLabel(focus.timestamp).replace(/^\w+ · /, "")} · ${clockOf(new Date(focus.timestamp))}`}
              </span>
            </div>
            <CaptionEditor key={focus.mediaId} value={focus.description} onSave={(text) => void saveDescription(focus, text)} />
            <div style={{ position: "relative", marginTop: 8 }}>
              <button type="button" className="kb" data-testid="daily-log-photo-location" onClick={() => setLocOpen((open) => !open)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 0, border: 0, background: "transparent", fontFamily: FONT_TEXT, fontSize: 12.5, fontWeight: focus.location ? 600 : 500, color: focus.location ? DL.ink : DL.muted, cursor: "pointer" }}>
                <MapPin size={12} strokeWidth={2} aria-hidden />
                {focus.location ? (focus.location.path || focus.location.name) : "Add location"}
              </button>
              {locOpen && (
                <LocationMenu
                  current={focus.location}
                  jobSiteId={jobSiteId}
                  visitId={walk.visitId}
                  search={search}
                  onPick={(location) => void saveLocation(focus, location)}
                  onClose={() => setLocOpen(false)}
                />
              )}
            </div>
          </div>
        )}
        {problem && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: DL.failInk }}>{problem}</div>}
      </div>
    </div>
  );
}

/** The focused photo's caption — plain prose, edited in place, saved on blur
 *  (Escape puts it back). An emptied caption is put back, never saved. */
function CaptionEditor({ value, onSave }: { value: string; onSave: (text: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <textarea
      data-testid="daily-log-photo-caption"
      aria-label="Photo caption"
      rows={2}
      value={draft}
      placeholder="Add a caption"
      className="review-editable"
      onInput={(event) => setDraft(event.currentTarget.value)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => { if (!draft.trim()) setDraft(value); else onSave(draft); }}
      onKeyDown={(event) => {
        if (event.key === "Escape") { setDraft(value); (event.target as HTMLTextAreaElement).blur(); }
        if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); (event.target as HTMLTextAreaElement).blur(); }
      }}
      style={{ display: "block", width: "calc(100% + 12px)", boxSizing: "border-box", margin: "10px -6px 0", padding: "2px 6px", border: 0, outline: "none", borderRadius: 6, resize: "none", fontFamily: FONT_TEXT, fontSize: 14, lineHeight: 1.5, color: DL.ink }}
    />
  );
}

const coverImage: CSSProperties = { display: "block", width: "100%", height: "100%", objectFit: "cover" };

const paneStyle: CSSProperties = {
  width: "100%", boxSizing: "border-box", minWidth: 0,
  background: DL.card, border: `1px solid ${DL.hairline}`, borderRadius: 16,
  position: "sticky", top: 12,
  display: "flex", flexDirection: "column",
  overflow: "hidden",
  animation: "dlPaneIn .18s cubic-bezier(.2,.8,.2,1) both",
};
